/**
 * The Line footer's tabs (GEN-3285), in the order the tab row shows them.
 * At a glance is first and the default.
 */
export const LINE_TABS = [
  'glance',
  'sales',
  'supply',
  'planning',
  'elsewhere',
  'vendor',
] as const;
export type LineTab = (typeof LINE_TABS)[number];

/** Tabs that read the line's item; a line with no SKU can't open them. */
const NEEDS_PRODUCT: readonly LineTab[] = ['sales', 'supply', 'elsewhere'];

const STORAGE_KEY = 'buyer-board.line-tab';

export function isLineTab(value: unknown): value is LineTab {
  return LINE_TABS.some((tab) => tab === value);
}

/**
 * The tab to open: the link's `lineTab` first, so a shared link or a reload
 * reopens it, then the viewer's last tab, then At a glance. Anything that is
 * not a tab (an old link, a tab since removed) falls through.
 */
export function resolveLineTab(
  fromUrl: string | undefined,
  stored: string | null,
): LineTab {
  if (isLineTab(fromUrl)) return fromUrl;
  if (isLineTab(stored)) return stored;
  return 'glance';
}

/**
 * The tab to show for this line: a tab it can't open (no SKU) falls back to
 * At a glance instead of an empty, disabled tab.
 */
export function lineTabFor(tab: LineTab, hasProduct: boolean): LineTab {
  return !hasProduct && NEEDS_PRODUCT.includes(tab) ? 'glance' : tab;
}

/** The viewer's last tab; storage can be missing or blocked. */
export function readStoredLineTab(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeLineTab(tab: LineTab): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, tab);
  } catch {
    // A convenience only: the URL still carries the tab.
  }
}
